import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ENVIRONMENT, filterBoolean } from '@core/common-legacy';
import { injectDevFlag } from '@core/dev-flags-logic';
import { DevFlag } from '@core/dev-flags-types';
import { FeatureSpecFacade } from '@core/feature-spec-logic';
import { FeatureCode } from '@core/feature-spec-types';
import { OfferStatus } from '@core/offers-types';
import { ReferralsSettingsEntitySelectors } from '@core/referrals-data';
import type { ReferralsSettings } from '@core/referrals-types';
import type { OfferStage, Stage } from '@core/stages-types';
import { StageCategory } from '@core/stages-types';

export interface ReferralsEnvironment {
  referralsProtocol: 'http' | 'https';
  referralsPartialDomain: string;
}

@Injectable({ providedIn: 'root' })
export class ReferralsService {
  private readonly featureSpecDataHelpers = inject(FeatureSpecFacade);
  private readonly store = inject(Store);
  private readonly environment = inject<ReferralsEnvironment>(ENVIRONMENT);

  public referralsSettings$: Observable<ReferralsSettings> = this.store
    .select(ReferralsSettingsEntitySelectors.data)
    .pipe(filterBoolean);
  public referralsPortalUrl$: Observable<string> = this.referralsSettings$.pipe(
    map(settings => this.createPortalUrl(settings.subdomain)),
  );

  private hasReferralsEntitled = this.featureSpecDataHelpers.selectIsEntitled(
    FeatureCode.Referrals,
  );
  private hasReferralsEnabled = this.featureSpecDataHelpers.selectIsEnabled(FeatureCode.Referrals);
  private hasReferralsFlag = injectDevFlag(DevFlag.Referrals);

  public hasReferralsAndReferralsFlag(): boolean {
    return this.hasReferralsEntitled() && this.hasReferralsFlag;
  }

  public isProgramEnabled(): boolean {
    return this.hasReferralsAndReferralsFlag() && this.hasReferralsEnabled();
  }

  public isReferralsEnabledForOfferStatus(offerStatus: OfferStatus): boolean {
    return ![OfferStatus.Archived, OfferStatus.Closed].includes(offerStatus);
  }

  public createPortalUrl(subdomain: string | null): string {
    const _subdomain = subdomain || 'example';
    const { referralsProtocol, referralsPartialDomain } = this.environment;
    return `${referralsProtocol}://${_subdomain}.${referralsPartialDomain}`;
  }

  public filterOutReferralStage(
    stages: OfferStage[] | Array<Partial<Stage>>,
  ): OfferStage[] | Array<Partial<Stage>> {
    return stages.filter(
      s =>
        s.category !== StageCategory.Referred ||
        (s.category === StageCategory.Referred && this.hasReferralsAndReferralsFlag()),
    );
  }

  public checkReferralsFeatureFlag(stages: Stage[]): Stage[] {
    return stages.filter(
      stage =>
        stage.category !== StageCategory.Referred ||
        (stage.category === StageCategory.Referred && this.hasReferralsAndReferralsFlag()),
    );
  }
}
